import { drawBustTexture } from "./bustTorso";

const REST_YAW = -15;
const MIN_YAW = -51;
export const MAX_YAW = 30;
const POINTER_YAW_RANGE = 51;
const clampYaw = yaw => Math.max(MIN_YAW, Math.min(MAX_YAW, yaw));

export function mountBustMotion(canvas, textures, frames, initialTarget = REST_YAW, torsoTextures = []) {
  const doc = canvas.ownerDocument, win = doc.defaultView;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Portrait canvas unavailable");
  const media = win.matchMedia?.("(prefers-reduced-motion: reduce)");
  let reduced = Boolean(media?.matches), current = REST_YAW;
  let target = reduced ? REST_YAW : clampYaw(initialTarget);
  let handle = null, previous = null, lastIndex = -1, destroyed = false;
  const cleanup = [];

  function listen(element, type, fn, options) {
    element.addEventListener(type, fn, options);
    cleanup.push(() => element.removeEventListener(type, fn, options));
  }
  function render() {
    let index = -1, distance = Infinity;
    for (let i = 0; i < frames.length; i++) {
      if (!textures[i]) continue;
      const delta = Math.abs(frames[i].yaw - current);
      if (delta < distance) { index = i; distance = delta; }
    }
    if (index === -1) return;
    const frame = frames[index], transform = frame.sprite_transform;
    if (index !== lastIndex) {
      // The torso replaces only the clipped lower region; the original head stays intact.
      drawBustTexture(context, textures[index], torsoTextures[index], transform, canvas.width, canvas.height);
      lastIndex = index;
      canvas.dataset.frameAngle = String(frame.angle);
      canvas.dataset.scale = transform.scale.toFixed(5);
      canvas.dataset.torsoScale = (frame.torso_scale ?? 1).toFixed(5);
      canvas.dataset.colorGrade = frame.color_grade ? "calibrated" : "original";
    }
    canvas.dataset.yaw = current.toFixed(2);
  }
  function stop() {
    if (handle !== null) win.cancelAnimationFrame(handle);
    handle = null; previous = null;
  }
  function start() {
    if (!destroyed && !doc.hidden && !reduced && handle === null && current !== target) {
      handle = win.requestAnimationFrame(tick);
    }
  }
  function tick(timestamp) {
    handle = null;
    if (destroyed || doc.hidden || reduced) return;
    const dt = previous === null ? 16 : Math.min(50, Math.max(0, timestamp - previous));
    previous = timestamp;
    const delta = (target - current) * (1 - Math.exp(-dt / 180));
    const maxStep = dt * 240 / 1000;
    current = clampYaw(current + Math.max(-maxStep, Math.min(maxStep, delta)));
    if (Math.abs(current - target) < 0.02) current = target;
    render();
    if (current !== target) start(); else previous = null;
  }
  function setTarget(value) {
    target = clampYaw(value);
    start();
  }
  function rest() { if (!destroyed) setTarget(REST_YAW); }
  function followPosition(clientX) {
    if (reduced || !Number.isFinite(clientX)) return;
    // Use the entire viewport, not the portrait rectangle.
    const center = win.innerWidth / 2;
    setTarget((clientX - center) / Math.max(1, center) * POINTER_YAW_RANGE);
  }
  listen(doc, "pointermove", event => {
    if (event.pointerType && !["mouse", "pen"].includes(event.pointerType)) return;
    followPosition(event.clientX);
  });
  function followTouch(event) {
    if (event.touches?.length !== 1) return;
    followPosition(event.touches[0].clientX);
  }
  // Native scrolling cancels touch pointermove; passive touch events keep tracking
  // without capturing the gesture or calling preventDefault. Lifting the finger
  // leaves the last pose in place, with no idle animation.
  listen(doc, "touchstart", followTouch, { passive: true });
  listen(doc, "touchmove", followTouch, { passive: true });
  listen(doc, "mouseout", event => { if (!event.relatedTarget) rest(); });
  listen(win, "blur", rest);
  listen(doc, "visibilitychange", () => {
    if (doc.hidden) { stop(); target = REST_YAW; }
    else { render(); start(); }
  });
  if (media) listen(media, "change", () => {
    reduced = media.matches;
    stop();
    if (reduced) { current = target = REST_YAW; render(); }
    else start();
  });
  render();
  start();
  const dispose = () => { destroyed = true; stop(); cleanup.forEach(remove => remove()); };
  // Newly decoded poses can improve a settled view without starting an idle RAF loop.
  dispose.refresh = () => { if (!destroyed) render(); };
  return dispose;
}
